import { inject, injectable } from 'tsyringe';
import { type ISupportUserRepository } from 'application/repositorySchemas/ISupportUserRepository';
import { type ISupportUserTokenRepository } from 'application/repositorySchemas/ISupportUserTokenRepository';
import { type ISupporterLoginByEmailClient } from 'interfaceAdapter/gatewaySchemas/ISupporterLoginByEmailClient';
import { type ISupportUserLogoutClient } from 'interfaceAdapter/gatewaySchemas/ISupportUserLogoutClient';
import {
    type IEditSupportUserProfileClient,
    IPostInput as IEditProfileInput,
} from 'interfaceAdapter/gatewaySchemas/IEditSupportUserProfileClient';
import { type ISupporterSendPasswordResetEmailClient } from 'interfaceAdapter/gatewaySchemas/ISupporterSendPasswordResetEmailClient';
import { type ISupportUserPasswordClient } from 'interfaceAdapter/gatewaySchemas/ISupportUserPasswordClient';

@injectable()
export class SupportUserRepository implements ISupportUserRepository {
    public readonly token: string;

    constructor(
        @inject('ISupportUserTokenRepository')
        public readonly supportUserToken: ISupportUserTokenRepository,
        @inject('ISupporterLoginByEmailClient')
        public readonly supporterLoginByEmail: ISupporterLoginByEmailClient,
        @inject('ISupportUserLogoutClient')
        public readonly supportUserLogoutClient: ISupportUserLogoutClient,
        @inject('IEditSupportUserProfileClient')
        public readonly editSupportUserProfile: IEditSupportUserProfileClient,
        @inject('ISupporterSendPasswordResetEmailClient')
        public readonly supporterSendPasswordResetEmailClient: ISupporterSendPasswordResetEmailClient,
        @inject('ISupportUserPasswordClient')
        public readonly supportUserPasswordClient: ISupportUserPasswordClient,
    ) {
        this.token = this.supportUserToken.get() ?? '';
    }

    public loginByEmail = async (email: string, password: string) => {
        const { data, message } = await this.supporterLoginByEmail.post({ email, password });
        if (message) {
            throw new Error(message);
        }
        this.supportUserToken.remove();
        this.supportUserToken.set(data.api_token);
        return data;
    };

    public logout = async () => {
        await this.supportUserLogoutClient.post({ token: this.token });
    };

    public editProfile = async (support_user: IEditProfileInput['support_user']) => {
        const { data, message } = await this.editSupportUserProfile.post({
            token: this.supportUserToken.get() || '',
            support_user,
        });

        if (message) {
            throw new Error(message);
        }

        return data;
    };

    public sendResetPasswordEmail = async (email: string) => {
        const { message } = await this.supporterSendPasswordResetEmailClient.post({ email });
        if (message) {
            throw new Error(message);
        }
    };

    public async resetPassword(code: string, password: string) {
        const { data, message } = await this.supportUserPasswordClient.post({
            code,
            password,
        });

        if (message) {
            throw new Error(message);
        }

        return { result: data };
    }
}
